import { enviroment } from "../enviroment";
import { PageQuery } from "./models/pageQuery";
import { getInitialQuery } from "../presentation/shared/functions/getInitialQuery.function";

// Page-size memory (flag REACT_APP_REMEMBER_PAGE_SIZE_ENABLED, Bait via Tom
// 1/10): the page size a user picks in a list footer is remembered per user
// and per list in localStorage and used as that list's default on later
// visits when the URL carries no pageSize. A URL that names a pageSize
// (shared link, Back/Forward) still wins, exactly as today.
//
// Key: `${b2CId}_pageSize_${listKey}` - the same per-user prefix CartContext
// uses, so an act-as admin keeps their own memory (the profile always
// returns the caller's b2CId). No profile = no memory. Only a value in the
// list's own set of offered sizes is ever read or written, so a corrupt or
// foreign value can never put a size in the footer's select that it does
// not offer. Every storage access is wrapped: a private window, blocked
// site data or a full quota is simply "nothing remembered".
//
// Nothing in this module runs while the flag is off: usePageQuery and the
// prefetch helpers below check it before calling in, and the helpers return
// today's query untouched.

// Mirrors the TableFooter fallback (`pageSizes ?? [12, 25, 50, 100]`); the
// vendor list passes its own [24, 48, 96].
export const DEFAULT_PAGE_SIZES: readonly number[] = [12, 25, 50, 100];

const KEY_INFIX = "_pageSize_";

export function pageSizeMemoryKey(b2CId: string, listKey: string): string {
  return `${b2CId}${KEY_INFIX}${listKey}`;
}

// The list key for a screen: its route path without surrounding slashes
// ("/logo" -> "logo", "/admin/price-import/" -> "admin/price-import").
// Filters, sorting and paging live in the query string, so one list keeps
// one key no matter what the user filtered on. "/" (and "") = no key.
export function listKeyFromPathname(pathname: string | undefined): string {
  return (pathname ?? "").replace(/^\/+|\/+$/g, "");
}

// Parses a stored value: an integer that the list offers, else undefined
// ("", "abc", "12.5", "1e2", a size the list does not offer).
function parseAllowedPageSize(
  raw: string | null | undefined,
  allowed: readonly number[]
): number | undefined {
  if (raw === null || raw === undefined) return undefined;
  if (!/^\d+$/.test(raw)) return undefined;
  const size = Number(raw);
  return allowed.includes(size) ? size : undefined;
}

function storage(): Storage | undefined {
  // Touching window.localStorage itself can throw (blocked site data).
  return typeof window === "undefined" ? undefined : window.localStorage;
}

export function readRememberedPageSize(
  b2CId: string | undefined,
  listKey: string | undefined,
  allowed: readonly number[]
): number | undefined {
  if (!b2CId || !listKey) return undefined;
  try {
    return parseAllowedPageSize(
      storage()?.getItem(pageSizeMemoryKey(b2CId, listKey)),
      allowed
    );
  } catch {
    return undefined;
  }
}

export function rememberPageSize(
  b2CId: string | undefined,
  listKey: string | undefined,
  size: number | undefined,
  allowed: readonly number[]
): void {
  if (!b2CId || !listKey) return;
  if (size === undefined || !allowed.includes(size)) return;
  try {
    storage()?.setItem(pageSizeMemoryKey(b2CId, listKey), String(size));
  } catch {
    // quota / private mode: the list still works, it is just not remembered
  }
}

// Boot prefetch only (App.tsx warms the landing list's first page in
// parallel with the profile call, so no b2CId is known yet): the one value
// every user of this browser remembered for the list. Two users with
// different memories = undefined (the caller keeps the default), and so is
// no memory at all. Never a source for the list itself - usePageQuery only
// reads the per-user key.
export function peekRememberedPageSizeAnyUser(
  listKey: string | undefined,
  allowed: readonly number[]
): number | undefined {
  if (!listKey) return undefined;
  try {
    const store = storage();
    if (!store) return undefined;
    const suffix = `${KEY_INFIX}${listKey}`;
    let found: number | undefined;
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (!key || !key.endsWith(suffix)) continue;
      const size = parseAllowedPageSize(store.getItem(key), allowed);
      if (size === undefined) continue;
      if (found !== undefined && found !== size) return undefined;
      found = size;
    }
    return found;
  } catch {
    return undefined;
  }
}

// The first-page query a cross prefetch must warm so its cache key matches
// what the target list computes on a fresh visit: the user's remembered size
// for that list, else the list's default. Flag off = today's
// getInitialQuery(new URLSearchParams(), fallback) untouched (no storage
// access).
export function prefetchListQuery(
  listKey: string,
  b2CId: string | undefined,
  fallbackPageSize?: number
): PageQuery {
  if (!enviroment.rememberPageSizeEnabled) {
    return getInitialQuery(new URLSearchParams(), fallbackPageSize);
  }
  return getInitialQuery(
    new URLSearchParams(),
    readRememberedPageSize(b2CId, listKey, DEFAULT_PAGE_SIZES) ??
      fallbackPageSize
  );
}

// Same for the boot prefetch (no profile yet): the browser's single
// remembered size for the list, else the default.
export function bootPrefetchListQuery(
  listKey: string,
  fallbackPageSize?: number
): PageQuery {
  if (!enviroment.rememberPageSizeEnabled) {
    return getInitialQuery(new URLSearchParams(), fallbackPageSize);
  }
  return getInitialQuery(
    new URLSearchParams(),
    peekRememberedPageSizeAnyUser(listKey, DEFAULT_PAGE_SIZES) ??
      fallbackPageSize
  );
}
